import React, { useEffect, useState, useMemo } from 'react';
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container,
  Paper,
  Box,
  Button,
  IconButton,
  Typography,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  TablePagination,
  Chip,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import { api } from '../../api/api';

import Modal from '../../components/Modal/Modal';
import Error from '../../components/Error/Error';
import Loading from '../../components/Loading/Loading';

const useStyles = makeStyles((theme) => ({
  table: { minWidth: 650 },
  compactTable: {
    minWidth: 650,
    '& .MuiTableCell-root': {
      padding: theme.spacing(1, 2),
    },
  },
  container: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(2),
  },
  topButtons: { display: 'flex', justifyContent: 'flex-end', marginBottom: theme.spacing(2) },
  modalButtons: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
    gap: theme.spacing(2),
  },
  scrollableTableContainer: {
    maxHeight: 400,
    overflow: 'auto',
  },
  declinedParamsSection: {
    marginTop: theme.spacing(4),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  searchField: {
    width: 250,
  },
}));

export default function DeclineSettingsPage({ onRender }) {
  const classes = useStyles();

  // Local state management
  const [declineSettings, setDeclineSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [topDeclinedParams, setTopDeclinedParams] = useState([]);
  const [topParamsLoading, setTopParamsLoading] = useState(true);
  const [topParamsError, setTopParamsError] = useState(false);

  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [settingToDelete, setSettingToDelete] = useState(null);

  // Pagination state for declined params
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Tracking config state
  const [trackingConfigs, setTrackingConfigs] = useState([]);
  const [trackingConfigsLoading, setTrackingConfigsLoading] = useState(true);
  const [offers, setOffers] = useState([]);
  const [newTrackingConfig, setNewTrackingConfig] = useState({
    parameterName: '',
    maxStreak: 20,
    offerIds: [],
  });

  const parameterNames = [
    'affId',
    'c1',
    'c2',
    'c3',
    'sub1',
    'click_id',
    'gclid',
    'address',
    'email',
    'emailDomain',
    'ipAddress',
  ];

  useEffect(() => {
    loadDeclineSettings();
    loadTopDeclinedParams();
    loadTrackingConfigs();
    loadOffers();
    onRender && onRender('Decline Settings');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadDeclineSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/decline-settings', {});
      setDeclineSettings(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading decline settings:', err);
      setError(true);
      setLoading(false);
    }
  };

  const loadTopDeclinedParams = async () => {
    setTopParamsLoading(true);
    try {
      const response = await api.get('/decline-counts/top');
      setTopDeclinedParams(response.data);
      setTopParamsLoading(false);
    } catch (err) {
      console.error('Error loading top declined parameters:', err);
      setTopParamsError(true);
      setTopParamsLoading(false);
    }
  };

  const createDeclineSetting = async (setting) => {
    try {
      const response = await api.post('/decline-settings', setting);
      setDeclineSettings([...declineSettings, response.data]);
      return response.data;
    } catch (err) {
      console.error('Error creating decline setting:', err);
      setError(true);
    }
  };

  const updateDeclineSetting = async (id, updatedSetting) => {
    try {
      const response = await api.patch(`/decline-settings/${id}`, updatedSetting);
      setDeclineSettings(declineSettings.map((setting) => (setting._id === id ? response.data : setting)));
    } catch (err) {
      console.error('Error updating decline setting:', err);
      setError(true);
    }
  };

  const deleteDeclineSetting = async (id) => {
    try {
      await api.delete(`/decline-settings/${id}`);
      setDeclineSettings(declineSettings.filter((setting) => setting._id !== id));
    } catch (err) {
      console.error('Error deleting decline setting:', err);
      setError(true);
    }
  };

  // Tracking Config CRUD
  const loadTrackingConfigs = async () => {
    setTrackingConfigsLoading(true);
    try {
      const response = await api.get('/decline-tracking-configs', {});
      setTrackingConfigs(response.data);
    } catch (err) {
      console.error('Error loading tracking configs:', err);
    } finally {
      setTrackingConfigsLoading(false);
    }
  };

  const loadOffers = async () => {
    try {
      const response = await api.get('/offers', {});
      setOffers(response.data);
    } catch (err) {
      console.error('Error loading offers:', err);
    }
  };

  const createTrackingConfig = async () => {
    if (!newTrackingConfig.parameterName) {
      alert('Please select a parameter name');
      return;
    }
    try {
      const response = await api.post('/decline-tracking-configs', newTrackingConfig);
      setTrackingConfigs([response.data, ...trackingConfigs]);
      setNewTrackingConfig({ parameterName: '', maxStreak: 20, offerIds: [] });
    } catch (err) {
      console.error('Error creating tracking config:', err);
    }
  };

  const updateTrackingConfig = async (id, updates) => {
    try {
      const response = await api.patch(`/decline-tracking-configs/${id}`, updates);
      setTrackingConfigs(trackingConfigs.map((c) => (c._id === id ? response.data : c)));
    } catch (err) {
      console.error('Error updating tracking config:', err);
    }
  };

  const deleteTrackingConfig = async (id) => {
    try {
      await api.delete(`/decline-tracking-configs/${id}`, {});
      setTrackingConfigs(trackingConfigs.filter((c) => c._id !== id));
    } catch (err) {
      console.error('Error deleting tracking config:', err);
    }
  };

  const handleDeleteSetting = (id) => {
    setDeleteModalOpen(true);
    setSettingToDelete(id);
  };

  const handleAddSetting = async () => {
    // Create a new setting with default values
    const newSettingData = {
      parameterName: '',
      parameterValue: '',
      active: false,
    };
    await createDeclineSetting(newSettingData);
  };

  const handleFieldChange = (id, field, value) => {
    setDeclineSettings(
      declineSettings.map((setting) => (setting._id === id ? { ...setting, [field]: value } : setting)),
    );
  };

  const handleParameterValueBlur = async (id) => {
    const setting = declineSettings.find((s) => s._id === id);
    if (setting) {
      await updateDeclineSetting(id, { parameterValue: setting.parameterValue });
    }
  };

  const handleActiveChange = async (id, value) => {
    const setting = declineSettings.find((s) => s._id === id);
    if (!setting.parameterName || !setting.parameterValue) {
      alert('Please fill in both Parameter Name and Parameter Value before activating.');
      return;
    }
    handleFieldChange(id, 'active', value);
    await updateDeclineSetting(id, { active: value });
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter and paginate declined params
  const filteredDeclinedParams = useMemo(() => {
    if (!searchQuery.trim()) {
      return topDeclinedParams;
    }
    const query = searchQuery.toLowerCase();
    return topDeclinedParams.filter(
      (item) =>
        item.parameterValue?.toLowerCase().includes(query) ||
        item.parameterName?.toLowerCase().includes(query),
    );
  }, [topDeclinedParams, searchQuery]);

  const paginatedDeclinedParams = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredDeclinedParams.slice(start, start + rowsPerPage);
  }, [filteredDeclinedParams, page, rowsPerPage]);

  // Reset page when search changes
  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  if (error) {
    return <Error error='An error occurred while loading decline settings.' />;
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Modal open={isDeleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <Box>
          <Typography variant='h6'>Are you sure you want to delete this setting?</Typography>
        </Box>
        <Box className={classes.modalButtons}>
          <Button variant='contained' color='primary' onClick={() => setDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button
            variant='contained'
            color='secondary'
            onClick={async () => {
              await deleteDeclineSetting(settingToDelete);
              setDeleteModalOpen(false);
            }}
          >
            Delete
          </Button>
        </Box>
      </Modal>

      <Container className={classes.container}>
        {/* Tracking Config Section */}
        <Box className={classes.declinedParamsSection} style={{ marginBottom: 32 }}>
          <Typography variant='h6' gutterBottom>
            Decline Tracking Configuration
          </Typography>
          <Typography variant='body2' color='textSecondary' style={{ marginBottom: 16 }}>
            Set max decline streaks per parameter. Offer-specific settings override "All Offers" defaults.
          </Typography>
          <TableContainer component={Paper}>
            <Table className={classes.compactTable} size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Parameter</TableCell>
                  <TableCell>Max Streak</TableCell>
                  <TableCell>Offers</TableCell>
                  <TableCell align='right'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {/* New config row */}
                <TableRow>
                  <TableCell>
                    <Select
                      value={newTrackingConfig.parameterName}
                      onChange={(e) =>
                        setNewTrackingConfig({ ...newTrackingConfig, parameterName: e.target.value })
                      }
                      displayEmpty
                      style={{ minWidth: 120 }}
                    >
                      <MenuItem value='' disabled>
                        Select...
                      </MenuItem>
                      {parameterNames.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type='number'
                      value={newTrackingConfig.maxStreak}
                      onChange={(e) =>
                        setNewTrackingConfig({
                          ...newTrackingConfig,
                          maxStreak: parseInt(e.target.value) || 1,
                        })
                      }
                      style={{ width: 80 }}
                      inputProps={{ min: 1 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Autocomplete
                      multiple
                      options={offers}
                      getOptionLabel={(option) => option.name || option._id}
                      value={offers.filter((o) => newTrackingConfig.offerIds.includes(o._id))}
                      onChange={(e, newValue) =>
                        setNewTrackingConfig({ ...newTrackingConfig, offerIds: newValue.map((o) => o._id) })
                      }
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip size='small' label={option.name || option._id} {...getTagProps({ index })} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={newTrackingConfig.offerIds.length === 0 ? 'All Offers' : ''}
                        />
                      )}
                      style={{ minWidth: 200 }}
                    />
                  </TableCell>
                  <TableCell align='right'>
                    <Button
                      variant='contained'
                      color='primary'
                      size='small'
                      startIcon={<AddIcon />}
                      onClick={createTrackingConfig}
                    >
                      Add
                    </Button>
                  </TableCell>
                </TableRow>
                {/* Existing configs */}
                {trackingConfigs.map((config) => (
                  <TableRow key={config._id} hover>
                    <TableCell>
                      <Select
                        value={config.parameterName}
                        onChange={(e) => updateTrackingConfig(config._id, { parameterName: e.target.value })}
                      >
                        {parameterNames.map((name) => (
                          <MenuItem key={name} value={name}>
                            {name}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type='number'
                        value={config.maxStreak}
                        onChange={(e) =>
                          updateTrackingConfig(config._id, { maxStreak: parseInt(e.target.value) || 1 })
                        }
                        style={{ width: 80 }}
                        inputProps={{ min: 1 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Autocomplete
                        multiple
                        options={offers}
                        getOptionLabel={(option) => option.name || option._id}
                        value={offers.filter((o) => config.offerIds.includes(o._id))}
                        onChange={(e, newValue) =>
                          updateTrackingConfig(config._id, { offerIds: newValue.map((o) => o._id) })
                        }
                        renderTags={(value, getTagProps) =>
                          value.length === 0 ? (
                            <Chip size='small' label='All Offers' color='default' />
                          ) : (
                            value.map((option, index) => (
                              <Chip
                                size='small'
                                label={option.name || option._id}
                                {...getTagProps({ index })}
                              />
                            ))
                          )
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder={config.offerIds.length === 0 ? 'All Offers' : ''}
                          />
                        )}
                        style={{ minWidth: 200 }}
                      />
                    </TableCell>
                    <TableCell align='right'>
                      <IconButton color='secondary' onClick={() => deleteTrackingConfig(config._id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <Box className={classes.topButtons}>
          <Button variant='contained' color='primary' startIcon={<AddIcon />} onClick={handleAddSetting}>
            Add Setting
          </Button>
        </Box>
        <TableContainer component={Paper} className={classes.scrollableTableContainer}>
          <Table className={classes.table} stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Parameter Name</TableCell>
                <TableCell>Parameter Value</TableCell>
                <TableCell>Active</TableCell>
                <TableCell align='right'>Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {declineSettings.map((setting) => (
                <TableRow key={setting._id} hover>
                  <TableCell>
                    <Select
                      value={setting.parameterName}
                      onChange={async (e) => {
                        handleFieldChange(setting._id, 'parameterName', e.target.value);
                        await updateDeclineSetting(setting._id, { parameterName: e.target.value });
                      }}
                    >
                      {parameterNames.map((name) => (
                        <MenuItem key={name} value={name}>
                          {name}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={setting.parameterValue}
                      onChange={(e) => handleFieldChange(setting._id, 'parameterValue', e.target.value)}
                      onBlur={async () => await handleParameterValueBlur(setting._id)}
                      helperText='Case sensitive'
                    />
                  </TableCell>
                  <TableCell>
                    <Checkbox
                      checked={setting.active}
                      onChange={async (e) => await handleActiveChange(setting._id, e.target.checked)}
                      disabled={!setting.parameterName || !setting.parameterValue}
                    />
                  </TableCell>
                  <TableCell align='right'>
                    <IconButton color='secondary' onClick={() => handleDeleteSetting(setting._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box className={classes.declinedParamsSection}>
          <Box className={classes.sectionHeader}>
            <Typography variant='h6'>Recent Declined Parameters</Typography>
            <TextField
              className={classes.searchField}
              placeholder='Search by value...'
              variant='outlined'
              size='small'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon style={{ marginRight: 8, color: 'rgba(0,0,0,0.54)' }} />,
              }}
            />
          </Box>
          {topParamsLoading ? (
            <Loading />
          ) : topParamsError ? (
            <Error error='An error occurred while loading declined parameters.' />
          ) : (
            <>
              <TableContainer component={Paper} className={classes.scrollableTableContainer}>
                <Table className={classes.compactTable} stickyHeader size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Parameter Name</TableCell>
                      <TableCell>Parameter Value</TableCell>
                      <TableCell>Decline Streak</TableCell>
                      <TableCell>Last Updated</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedDeclinedParams.map((item, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{item.parameterName}</TableCell>
                        <TableCell>{item.parameterValue}</TableCell>
                        <TableCell>{item.declineStreak}</TableCell>
                        <TableCell>
                          {item.lastUpdated ? new Date(item.lastUpdated).toLocaleString() : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component='div'
                count={filteredDeclinedParams.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </>
          )}
        </Box>
      </Container>
    </>
  );
}
